import { Link } from "react-router";
import { format, isToday } from "date-fns";
import { cn } from "@/lib/cn";
import { WEEK_ABBR, activeApptsOnDay, monthGridDays, type WeekAppointment } from "@/lib/dashboard-week";
import { AppointmentPill } from "./AppointmentPill";
import { ContagemDoDia } from "./ContagemDoDia";

const MAX_PILLS_PER_DAY = 3;

type Props = {
  appts: WeekAppointment[];
  /** false (padrão) = agrupado, só "N consultas". true = pílula individual por consulta. */
  showAll: boolean;
  onSelectAppt: (appt: WeekAppointment) => void;
  /** Quando true, as células deixam de navegar pra /agenda (organização ativa). */
  readOnly?: boolean;
};

/** Visão "Mês" — calendário de verdade (grade 7×N). Por padrão só a contagem por dia (agrupado);
 *  com "Mostrar tudo" ligado, cada consulta vira uma pílula clicável (mesmo componente da semana). */
export function MonthCalendarGrid({ appts, showAll, onSelectAppt, readOnly = false }: Props) {
  const days = monthGridDays();

  return (
    <div className="w-full">
      <div className="grid grid-cols-7 gap-1">
        {WEEK_ABBR.map((label) => (
          <div
            key={label}
            className="pb-1 text-center text-xs font-semibold uppercase tracking-wide text-zinc-400 dark:text-muted-foreground"
          >
            {label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map(({ date, isCurrentMonth }) => {
          const dayAppts = activeApptsOnDay(appts, date);
          const today = isToday(date);

          const dayNumber = (
            <span
              className={cn(
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm",
                today ? "bg-brand-600 font-bold text-white" : "text-zinc-700 dark:text-foreground/80",
              )}
            >
              {format(date, "d")}
            </span>
          );

          const cellShell = cn(
            "flex min-h-24 w-full flex-col rounded-lg border border-zinc-100 p-1.5 transition-colors dark:border-border/50",
            today && "border-brand-200 bg-brand-50/40 dark:border-primary/30 dark:bg-primary/10",
            !isCurrentMonth && "opacity-40",
          );

          if (!showAll) {
            const href = readOnly ? null : dayAppts.length > 0 ? "/agenda" : null;
            const cellContent = (
              <div className={cellShell}>
                <div className="flex justify-end">{dayNumber}</div>
                <div className="mt-1 flex flex-1 flex-col items-start gap-1">
                  <ContagemDoDia total={dayAppts.length} singular="consulta" plural="consultas" />
                </div>
              </div>
            );
            return href ? (
              <Link key={date.toISOString()} to={href}>
                {cellContent}
              </Link>
            ) : (
              <div key={date.toISOString()}>{cellContent}</div>
            );
          }

          return (
            <div key={date.toISOString()} className={cellShell}>
              <div className="flex justify-end">{dayNumber}</div>
              <div className="mt-1 flex w-full flex-1 flex-col gap-1">
                {dayAppts.slice(0, MAX_PILLS_PER_DAY).map((a) => (
                  <AppointmentPill key={a.id} appt={a} onSelect={onSelectAppt} />
                ))}
                {dayAppts.length > MAX_PILLS_PER_DAY && (
                  readOnly ? (
                    <p className="text-center text-[10px] font-medium text-zinc-400 dark:text-muted-foreground">
                      +{dayAppts.length - MAX_PILLS_PER_DAY}
                    </p>
                  ) : (
                    <Link
                      to="/agenda"
                      className="text-center text-[10px] font-medium text-brand-700 hover:underline dark:text-primary"
                    >
                      +{dayAppts.length - MAX_PILLS_PER_DAY}
                    </Link>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
